import { useEffect, useState, type FormEvent } from "react";
import { api } from "../api";
import { Link, useNavigate, Navigate } from "react-router-dom";
import { useAuth } from "../auth/AuthProvider";
import { AuthRahmen } from "../components/Logo";
import { LoadingScreen } from "../components/LoadingScreen";
import { CodeFeld } from "../auth/CodeFeld";
import { kontoFehler } from "../auth/fehler";

export function LoginPage(): JSX.Element {
  const { user, loading, login, loginCode } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [needsSetup, setNeedsSetup] = useState(false);
  // Zweiter Schritt (Zwei-Faktor): die Challenge vom Server und der Code.
  const [challenge, setChallenge] = useState<string | null>(null);
  const [code, setCode] = useState("");
  const [wiederherstellung, setWiederherstellung] = useState(false);

  useEffect(() => {
    api.setupStatus().then((s) => setNeedsSetup(s.needs_setup)).catch(() => undefined);
  }, []);

  if (loading) return <LoadingScreen />;
  if (user) return <Navigate to="/dashboard" replace />;
  if (needsSetup) return <Navigate to="/setup" replace />;

  const onSubmit = async (e: FormEvent<HTMLFormElement>): Promise<void> => {
    e.preventDefault();
    setError(null);
    setBusy(true);
    try {
      const weiter = await login(email, password);
      if (weiter) {
        setChallenge(weiter.challenge);
        setCode("");
        return;
      }
      navigate("/dashboard", { replace: true });
    } catch (err) {
      setError(kontoFehler(err));
    } finally {
      setBusy(false);
    }
  };

  const pruefe = async (wert: string): Promise<void> => {
    if (!challenge || busy) return;
    setError(null);
    setBusy(true);
    try {
      await loginCode(challenge, wert);
      navigate("/dashboard", { replace: true });
    } catch (err) {
      const text = kontoFehler(err);
      setError(text);
      setCode("");
      // Zwischenschritt abgelaufen: zurück zum Passwort.
      if (text.includes("zu lange gedauert")) {
        setChallenge(null);
        setPassword("");
      }
    } finally {
      setBusy(false);
    }
  };

  if (challenge) {
    return (
      <AuthRahmen>
        <form onSubmit={(e) => { e.preventDefault(); void pruefe(code); }} className="card space-y-5 p-6 shadow-lg sm:p-8">
          <div>
            <span className="mb-3 flex h-11 w-11 items-center justify-center rounded-xl bg-brand-primary/10 text-brand-primary ring-1 ring-inset ring-brand-primary/20">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <rect x="6" y="2" width="12" height="20" rx="2.5" /><path d="M11 18h2" />
              </svg>
            </span>
            <h1 className="text-2xl font-semibold tracking-tight text-primary">Bestätigungscode</h1>
            <p className="mt-1 text-sm text-muted">
              {wiederherstellung
                ? "Gib einen deiner Wiederherstellungscodes ein. Jeder gilt nur einmal."
                : "Öffne deine Authenticator-App und gib den sechsstelligen Code für diese Suite ein."}
            </p>
          </div>
          <CodeFeld
            key={wiederherstellung ? "r" : "t"}
            value={code}
            autoFocus
            wiederherstellung={wiederherstellung}
            onChange={(v) => {
              setCode(v);
              // Sechs Ziffern sind vollständig – gleich prüfen, wie man es von anderen Diensten kennt.
              if (!wiederherstellung && v.length === 6) void pruefe(v);
            }}
          />
          {error && <div className="callout callout-danger" role="alert">{error}</div>}
          <button type="submit" disabled={busy || (!wiederherstellung && code.length !== 6)} className="btn btn-primary btn-lg w-full">
            {busy ? "Prüfe …" : "Bestätigen"}
          </button>
          <div className="flex flex-wrap items-center justify-between gap-2 text-sm">
            <button
              type="button"
              className="text-muted underline-offset-2 hover:text-primary hover:underline"
              onClick={() => { setChallenge(null); setError(null); setPassword(""); }}
            >
              ← Zurück
            </button>
            <button
              type="button"
              className="font-medium text-brand-primary underline-offset-2 hover:underline"
              onClick={() => { setWiederherstellung(!wiederherstellung); setCode(""); setError(null); }}
            >
              {wiederherstellung ? "Code aus der App verwenden" : "Handy nicht zur Hand?"}
            </button>
          </div>
          {wiederherstellung && (
            <p className="text-xs text-muted">Keine Codes mehr? Deine Radioleitung kann die Zwei-Faktor-Anmeldung unter „Team &amp; Rollen“ zurücksetzen.</p>
          )}
        </form>
      </AuthRahmen>
    );
  }

  return (
    <AuthRahmen>
      <form onSubmit={onSubmit} className="card space-y-5 p-6 shadow-lg sm:p-8">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight text-primary">Anmeldung</h1>
          <p className="mt-1 text-sm text-muted">Willkommen zurück – melde dich mit deinem Zugang an.</p>
        </div>
        <label className="block">
          <span className="field-label">E-Mail oder Username</span>
          <input
            type="text"
            autoComplete="username"
            required
            autoFocus
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="input"
          />
        </label>
        <label className="block">
          <span className="flex items-baseline justify-between gap-2">
            <span className="field-label">Passwort</span>
            <Link to="/passwort-vergessen" className="text-xs font-medium text-brand-primary underline-offset-2 hover:underline">
              Passwort vergessen?
            </Link>
          </span>
          <input
            type="password"
            autoComplete="current-password"
            required
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="input"
          />
        </label>
        {error && <div className="callout callout-danger" role="alert">{error}</div>}
        <button type="submit" disabled={busy} className="btn btn-primary btn-lg w-full">
          {busy ? "Bitte warten …" : "Anmelden"}
        </button>
      </form>
    </AuthRahmen>
  );
}
